import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthProvider";
import axios from "axios";

const StripeForm = ({ products, payFinalAmount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useContext(AuthContext);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [error, setError] = useState(null);

  const personEmail = products?.email;
  const allSet = products?.set || [];

  const calculateTotalPrice = (pro) => {
    let total = pro.quantity * pro.selectedPricingValue;

    // Add tax if applicable
    if (pro?.tax) {
      const taxValue = (parseFloat(pro.tax) / 100) * total;
      total += taxValue;
    }

    // Apply discount if available
    if (pro?.discount) {
      const discountValue =
        pro.discountType === "%"
          ? (parseFloat(pro.discount) / 100) * total
          : parseFloat(pro.discount);

      total -= discountValue;
    }

    // Ensure total is not negative
    return Math.max(0, total).toFixed(2);
  };

  const calculateAllSetTotal = (allSet) => {
    return allSet
      .reduce((total, perSet) => {
        const setTotal = perSet.products.reduce(
          (acc, pro) => acc + parseFloat(calculateTotalPrice(pro)),
          0
        );
        return total + setTotal;
      }, 0)
      .toFixed(2);
  };

  const calculateFinalTotal = (allSet, products) => {
    // Calculate the total for all sets
    const total = calculateAllSetTotal(allSet);

    // Get the discount value and type
    const discount = parseFloat(products?.AllProductDiscount) || 0;
    const discountType = products?.AllProductDiscountType;

    let finalTotal = parseFloat(total);

    // Apply discount based on the discount type
    if (discountType === "$") {
      // Subtract fixed amount discount
      finalTotal -= discount;
    } else if (discountType === "%") {
      // Subtract percentage discount
      finalTotal -= (discount / 100) * finalTotal;
    }

    // Ensure the final total is not negative
    return Math.max(0, finalTotal).toFixed(2);
  };

  const calculateDeposit = (finalTotal, advanceDeposit) => {
    // Convert advanceDeposit to a percentage value
    const depositPercentage = parseFloat(advanceDeposit) || 0;

    // Calculate the deposit amount
    const depositAmount = (depositPercentage / 100) * parseFloat(finalTotal);

    // Ensure the deposit amount is not negative
    return Math.max(0, depositAmount).toFixed(2);
  };

  const calculateAmountDue = (allSet, products, totalPayAmount) => {
    const finalTotal = parseFloat(calculateFinalTotal(allSet, products)); // Call the function
    return (finalTotal - totalPayAmount).toFixed(2); // Subtract totalPayAmount
  };

  const [paymentData, setPaymentData] = useState([]);
  const [totalPayAmount, setTotalPayAmount] = useState(0);

  useEffect(() => {
    if (personEmail) {
      const fetchPaymentData = async () => {
        try {
          const response = await axios.get(
            `https://server-gs.vercel.app/paid/stripe/${personEmail}`
          );
          setPaymentData(response.data);

          // Calculate the total payAmount if available
          const total = response.data.reduce(
            (acc, payment) => acc + (parseFloat(payment.payAmount) || 0),
            0
          );
          setTotalPayAmount(total?.toFixed(2)); // Ensure two decimal places
        } catch (err) {
          console.error("Error fetching payment data:", err);
          setError("Failed to fetch payment data");
        } finally {
          setIsLoading(false); // Set loading to false after fetch completes
        }
      };

      fetchPaymentData();
    }
  }, [personEmail]);

  const payableAmounts = calculateDeposit(
    calculateFinalTotal(allSet, products),
    products?.AdvanceDeposit
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (!stripe || !elements) {
      setErrorMessage("Stripe.js has not loaded yet. Please try again later.");
      setIsLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const payableAmount = payFinalAmount;

    try {
      const response = await fetch(
        "https://server-gs.vercel.app/create-payment-intent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ payAmount: payableAmount }),
        }
      );

      const { clientSecret } = await response.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message);
      } else {
        if (result.paymentIntent.status === "succeeded") {
          setSuccessMessage("Payment successful! Thank you for your purchase.");

          const paymentData = {
            stripePaymentId: result.paymentIntent.id,
            invoicePaymentId: products?.paymentId,
            email: products?.email,
            name,
            address,
            phoneNumber,
            payAmount: payableAmount,
          };

          // Send payment data to backend for storage
          await fetch("https://server-gs.vercel.app/stripe-pay", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(paymentData),
          });

          // Send payment confirmation email
          await fetch("https://server-gs.vercel.app/sent-stripe-payment-confirmation", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: name.split(' ')[0],
              lastName: name.split(' ').slice(1).join(' '),
              email: products?.email,
              phone: phoneNumber,
              message: `Payment confirmation for invoice ${products?.paymentId}. Amount paid: $${payableAmount}.`,
              payAmount: payableAmount, // Fallback to current date if not available
              stripePaymentId: result.paymentIntent.id,
              invoicePaymentId: products?.paymentId
            }),
          });

          // Update order with the payAmount
          const existingPaidAmount = parseFloat(products?.paidStripeAmount) || 0;
          const newPaidAmount = existingPaidAmount + parseFloat(payableAmount);

          // Calculate the amount due
          const amountDue = calculateAmountDue(
            allSet,
            products,
            totalPayAmount
          );

          // Determine the status based on the updated paidStripeAmount
          let status = "partial";
          if (newPaidAmount >= parseFloat(amountDue)) {
            status = "paid";
          }

          // Update the order with the new paidStripeAmount and status
          await fetch(
            `https://server-gs.vercel.app/order-paid/${products?._id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                paidStripeAmount: newPaidAmount.toString(),
                status,
              }),
            }
          );

          // Reload the page to reflect the changes
          window.location.reload();
        }
      }
    } catch (error) {
      setErrorMessage("An error occurred while processing the payment.");
      console.error("Payment error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white shadow-md rounded-md">
      <h2 className="text-xl font-bold text-center text-gray-700 mb-4">
        Secure Payment Form
      </h2>
      {errorMessage && (
        <div className="bg-red-100 text-red-600 p-2 mb-4 rounded">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-100 text-green-600 p-2 mb-4 rounded">
          {successMessage}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-gray-600">Full Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-gray-600">Address</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St, City, State"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-gray-600">Phone Number</label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="(123) 456-7890"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-gray-600">Card Details</label>
          <div className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={!stripe || isLoading}
          className={`w-full py-2 text-white rounded-md transition-colors ${
            isLoading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isLoading ? "Processing..." : `Pay $ ${payFinalAmount}`}
        </button>
      </form>
    </div>
  );
};

export default StripeForm;
